import { create } from 'zustand';
import type { GameState, FrameResult } from '../engine/types';
import {
  createInitialState,
  potBall,
  foul,
  freeBall,
  concedeFrame,
  missCalled,
  switchPlayer,
  undo,
  endFrame,
} from '../engine/snookerEngine';
import type { BallColor } from '../engine/types';
import { games as gamesApi, frames as framesApi } from '../lib/api';
import { useSettingsStore } from './settingsStore';
import { usePricingStore, getActivePrice } from './pricingStore';

interface GameStoreState {
  gameState: GameState | null;
  gameId: number | null;
  tableId: number | null;
  staffId: number | null;
  pricePerFrameSnapshot: number;
  frameStartTime: number | null;
  completedFrames: FrameResult[];
  isGameActive: boolean;

  startGame: (player1Name: string, player2Name: string, framesTarget: number, staffId: number, tableId?: number | null) => Promise<void>;
  potBall: (ball: BallColor) => void;
  foul: (ballValue?: number) => void;
  freeBall: () => void;
  missCalled: () => void;
  switchPlayer: () => void;
  concedeFrame: () => void;
  undo: () => void;
  nextFrame: () => Promise<FrameResult | null>;
  endGame: () => Promise<void>;
  abandonGame: () => Promise<void>;
  resetFrame: () => void;
  replacePlayer: (playerIndex: 0 | 1, newName: string) => void;
  endAndRestartWithNewChallenger: (loserIndex: 0 | 1, newPlayerName: string) => Promise<void>;
  reset: () => void;
}

// Debounced live-score sync so rapid pots don't flood the server
let _liveScoreTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleLiveScoreSync(gameId: number, state: GameState) {
  if (_liveScoreTimer) clearTimeout(_liveScoreTimer);
  _liveScoreTimer = setTimeout(() => {
    void gamesApi.updateLiveScore(
      gameId,
      state.players[0].frameScore,
      state.players[1].frameScore,
      state.redsRemaining,
      state.phase,
      state.activePlayer
    );
  }, 400);
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  gameState: null,
  gameId: null,
  tableId: null,
  staffId: null,
  pricePerFrameSnapshot: 0,
  frameStartTime: null,
  completedFrames: [],
  isGameActive: false,

  startGame: async (player1Name, player2Name, framesTarget, staffId, tableId) => {
    const settings = useSettingsStore.getState();
    const { frame: priceFrame } = getActivePrice(
      usePricingStore.getState().slots,
      settings.getPricePerFrame(),
      settings.getPricePerRack()
    );
    const game = await gamesApi.create({
      player1Name,
      player2Name,
      staffId,
      tableId: tableId ?? null,
      framesTarget,
      pricePerFrameSnapshot: priceFrame,
      currencySnapshot: settings.settings.currency,
    });
    set({
      gameState: createInitialState(player1Name, player2Name, framesTarget),
      gameId: game.id,
      tableId: game.tableId ?? null,
      staffId,
      pricePerFrameSnapshot: game.pricePerFrameSnapshot,
      frameStartTime: Date.now(),
      completedFrames: [],
      isGameActive: true,
    });
  },

  potBall: (ball) => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const next = potBall(gameState, ball, useSettingsStore.getState().getUndoLimit());
    set({ gameState: next });
    if (gameId) scheduleLiveScoreSync(gameId, next);
  },

  foul: (ballValue) => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const next = foul(gameState, ballValue, useSettingsStore.getState().getUndoLimit());
    set({ gameState: next });
    if (gameId) scheduleLiveScoreSync(gameId, next);
  },

  freeBall: () => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const next = freeBall(gameState, useSettingsStore.getState().getUndoLimit());
    set({ gameState: next });
    if (gameId) scheduleLiveScoreSync(gameId, next);
  },

  missCalled: () => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const next = missCalled(gameState, useSettingsStore.getState().getUndoLimit());
    set({ gameState: next });
    if (gameId) scheduleLiveScoreSync(gameId, next);
  },

  switchPlayer: () => {
    const { gameState } = get();
    if (!gameState) return;
    set({ gameState: switchPlayer(gameState, useSettingsStore.getState().getUndoLimit()) });
  },

  concedeFrame: () => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const next = concedeFrame(gameState, useSettingsStore.getState().getUndoLimit());
    set({ gameState: next });
    if (gameId) scheduleLiveScoreSync(gameId, next);
  },

  undo: () => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const next = undo(gameState);
    set({ gameState: next });
    if (gameId) scheduleLiveScoreSync(gameId, next);
  },

  nextFrame: async () => {
    const { gameState, gameId, frameStartTime, completedFrames } = get();
    if (!gameState || !gameId) return null;

    const durationSeconds = frameStartTime
      ? Math.floor((Date.now() - frameStartTime) / 1000)
      : undefined;

    const { state: nextState, frameResult } = endFrame(gameState);

    const p1Frames = nextState.players[0].framesWon;
    const p2Frames = nextState.players[1].framesWon;

    await Promise.all([
      framesApi.create({
        gameId,
        frameNumber: frameResult.frameNumber,
        winnerName: frameResult.winnerName,
        player1Score: frameResult.player1Score,
        player2Score: frameResult.player2Score,
        highestBreak: frameResult.highestBreak,
        highestBreakPlayer: frameResult.highestBreakPlayer ?? undefined,
        durationSeconds,
      }),
      gamesApi.updateFrame(gameId, {
        player1Frames: p1Frames,
        player2Frames: p2Frames,
        framesPlayed: p1Frames + p2Frames,
      }),
    ]);

    set({ gameState: nextState, frameStartTime: Date.now(), completedFrames: [...completedFrames, frameResult] });
    // Reset live state for the new frame
    void gamesApi.updateLiveScore(gameId, 0, 0, 15, 'reds', nextState.activePlayer);
    return frameResult;
  },

  endGame: async () => {
    const { gameState, gameId, pricePerFrameSnapshot } = get();
    if (!gameState || !gameId) return;

    const p0 = gameState.players[0];
    const p1 = gameState.players[1];
    const winnerIndex = p0.framesWon > p1.framesWon ? 0 : p1.framesWon > p0.framesWon ? 1 : (gameState.activePlayer === 0 ? 1 : 0);
    const loserIndex = winnerIndex === 0 ? 1 : 0;
    const framesPlayed = p0.framesWon + p1.framesWon;
    const amountOwed = framesPlayed * pricePerFrameSnapshot;

    await gamesApi.complete(gameId, {
      winnerName: gameState.players[winnerIndex].name,
      loserName: gameState.players[loserIndex].name,
      amountOwed,
    });

    set({ gameState: null, gameId: null, tableId: null, pricePerFrameSnapshot: 0, isGameActive: false, completedFrames: [] });
  },

  abandonGame: async () => {
    const { gameId } = get();
    if (gameId) await gamesApi.abandon(gameId);
    set({ gameState: null, gameId: null, tableId: null, isGameActive: false, completedFrames: [] });
  },

  replacePlayer: (playerIndex, newName) => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const players = gameState.players.map((p, i) =>
      i === playerIndex ? { ...p, name: newName } : p
    ) as typeof gameState.players;
    set({ gameState: { ...gameState, players } });
    if (gameId) void gamesApi.updatePlayerName(gameId, playerIndex, newName);
  },

  endAndRestartWithNewChallenger: async (loserIndex, newPlayerName) => {
    const { gameState, gameId, tableId, staffId } = get();
    if (!gameState || !gameId || !staffId) return;

    const winnerIndex = (1 - loserIndex) as 0 | 1;
    const framesPlayed = gameState.players[0].framesWon + gameState.players[1].framesWon;
    const { pricePerFrameSnapshot } = get();
    const amountOwed = framesPlayed * pricePerFrameSnapshot;

    // Complete the current game
    await gamesApi.complete(gameId, {
      winnerName: gameState.players[winnerIndex].name,
      loserName: gameState.players[loserIndex].name,
      amountOwed,
    });

    // Winner always takes Player 1 (card left), new challenger always Player 2 (card right)
    const winnerName = gameState.players[winnerIndex].name;
    const p1Name = winnerName;
    const p2Name = newPlayerName;
    const settings = useSettingsStore.getState();
    const { frame: newPrice } = getActivePrice(usePricingStore.getState().slots, settings.getPricePerFrame(), settings.getPricePerRack());
    const newGame = await gamesApi.create({
      player1Name: p1Name,
      player2Name: p2Name,
      staffId,
      tableId: tableId ?? null,
      gameType: 'snooker',
      framesTarget: 0,
      pricePerFrameSnapshot: newPrice,
      currencySnapshot: settings.settings.currency,
    });

    // createInitialState resets ALL game state fields — phase, reds, colors, undo stack, etc.
    // Spreading the old gameState was keeping stale phase/ball data and breaking color scoring.
    set({
      gameId: newGame.id,
      tableId: newGame.tableId ?? null,
      pricePerFrameSnapshot: newGame.pricePerFrameSnapshot,
      frameStartTime: Date.now(),
      completedFrames: [],
      isGameActive: true,
      gameState: createInitialState(p1Name, p2Name, 0),
    });
  },

  reset: () => set({
    gameState: null, gameId: null, tableId: null, staffId: null,
    frameStartTime: null, completedFrames: [], isGameActive: false,
  }),

  resetFrame: () => {
    const { gameState } = get();
    if (!gameState) return;
    const fresh = createInitialState(
      gameState.players[0].name,
      gameState.players[1].name,
      gameState.framesTarget
    );
    fresh.players[0].framesWon = gameState.players[0].framesWon;
    fresh.players[1].framesWon = gameState.players[1].framesWon;
    fresh.frameNumber = gameState.frameNumber;
    set({ gameState: fresh, frameStartTime: Date.now() });
  },
}));
